import {Button, ButtonSize, ButtonStyle} from '@mintdata-wsp/common/components/button/Button';
import {MDIcon, MDIconSize} from '@mintdata-wsp/common/components/icon/MDIcon';
import {MDIcons} from '@mintdata-wsp/common/components/icon/MDIcons';
import {Spinner, SpinnerSize, SpinnerStyle, SpinnerWeight} from '@mintdata-wsp/common/components/spinner/Spinner';
import {ListTypes, PageInfo, SheetInfo} from '@mintdata-wsp/common/state/ui/visualDesigner/CopyPagesState';
import {WSPIcon} from '@mintdata-wsp/resources/icons/WSPIcon';
import {WSPIcons, WSPIconSize} from '@mintdata-wsp/resources/icons/WSPIcons';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {Row} from '@mintdata-wsp/wsp/ui/components/forms/Row';
import {Scrollable, ScrollBehavior} from '@mintdata-wsp/wsp/ui/components/scrollable/Scrollable';
import {OrganizationsDropdown} from '@mintdata-wsp/wsp/ui/views/visualDesigner/surface/panels/outline/copyPages/organizationsDropdown/OrganizationsDropdown';
import {SpaceDesignDropdown} from '@mintdata-wsp/wsp/ui/views/visualDesigner/surface/panels/outline/copyPages/spaceDesignDropdown/SpaceDesignDropdown';
import {VersionsDropdown} from '@mintdata-wsp/wsp/ui/views/visualDesigner/surface/panels/outline/copyPages/versionsDropdown/VersionsDropdown';
import {SurfaceOutlinePanelPartWrapper} from '@mintdata-wsp/wsp/ui/views/visualDesigner/surface/panels/outline/SurfaceOutlinePanelPartWrapper';
import {LocalStateUtil} from '@mintdata-wsp/wsp/util/LocalStateUtil';
import {Tooltip} from '@mintdata/mint-kit/impl/components/tooltip/Tooltip';
import {Checkbox, CheckboxStyle} from '@mintdata/mint-kit/impl/properties/components/checkbox/Checkbox';
import {InputEdit} from '@mintdata/mint-kit/impl/properties/components/inputEdit/InputEdit';
import bind from 'bind-decorator';
import * as classnames from 'classnames/bind';
import {action, computed, observable} from 'mobx';
import {observer} from 'mobx-react';
import * as React from 'react';
import ResizeSensor from 'css-element-queries/src/ResizeSensor';

const styles = require('./SurfaceCopyPagesOutline.less');
const cx = classnames.bind(styles);

interface ISurfaceCopyOutlineState
{
    isDetailsExpanded:boolean;
}

interface SurfaceCopyOutlineProps
{
    onExpand?:() => void,
    onCollapse?:() => void,
    className?:string,
}

@observer
export class SurfaceCopyPagesOutline extends React.Component<SurfaceCopyOutlineProps, ISurfaceCopyOutlineState>
{
    @observable
    private _state:ISurfaceCopyOutlineState = {
        isDetailsExpanded: false,
    };

    private _expandedFooterPart:HTMLElement | null = null;
    private _resizeSensor:ResizeSensor;

    @bind
    private _expandedFooterPartRef(ref:HTMLElement | null):void
    {
        this._expandedFooterPart = ref;

        if( this._expandedFooterPart )
        {
            this._resizeSensor = new ResizeSensor(this._expandedFooterPart, this._onContentResize);
        }
    }

    @bind
    private _onContentResize():void
    {
        if( this._expandedFooterPart )
        {
            const {workbenchVisibility: wv} = controllers.ui;
            wv.setSurfaceCopyPagesPanelExpandedPartHeight(this._expandedFooterPart.clientHeight);
        }
    }

    public componentWillUnmount():void
    {
        const {workbenchVisibility: wv} = controllers.ui;
        wv.setSurfaceCopyPagesPanelExpandedPartHeight(0);

        if( this._resizeSensor )
        {
            this._resizeSensor.detach(this._onContentResize);
            this._resizeSensor = null;
        }
    }

    render()
    {
        const {copyPages} = controllers.ui;

        const copyState = copyPages.getState();

        if( !copyState )
            return null;

        const {
            designState: {isCopyRelatedSheets, copyNamePrefix},
            isCopyingInProgress,
            shouldShowLoader,
            currentListType
        } = copyState;
        const {isDetailsExpanded} = this._state;
        const {className} = this.props;
        const isPagesList:boolean = currentListType === ListTypes.Pages;

        const copyBtnCaption:string = isPagesList
            ? `Copy Pages${this._selectedPagesCount ? ' (' + this._selectedPagesCount + ')' : ''}`
            : `Copy Sheets${this._selectedSheetsCount ? ' (' + this._selectedSheetsCount + ')' : ''}`;

        const isCopyBtnDisabled:boolean = isCopyingInProgress || isPagesList
            ? this._selectedPagesCount === 0
            : this._selectedSheetsCount === 0;

        return (
            <SurfaceOutlinePanelPartWrapper
                className={cx('base', {[className]: !!className})}
                title={this._renderHeader()}
                isCollapsible={false}
                rightActionItems={[{
                    tooltipText: 'Close',
                    icon: MDIcons.common.Close,
                    handler: this._onCloseClick
                }]}
            >
                <div className={cx('contentWrapper')}>
                    <div className={cx('header')}>
                        <SpaceDesignDropdown className={cx('spaceDesigns')} />
                        <VersionsDropdown className={cx('versions')} />
                    </div>
                    <div className={cx('scrollableWrapper')}>
                        <Scrollable
                            isScrollX={true}
                            isScrollY={true}
                            className={cx('itemsList')}
                            behavior={ScrollBehavior.OnHover}
                        >
                            {this._renderItems()}
                        </Scrollable>
                    </div>

                    <div className={cx('footer')}>
                        <Row className={cx('visiblePart')}>
                            <div
                                className={cx('expandControl')}
                                onClick={this._toggleDetailsExpand}
                            >
                                <WSPIcon
                                    className={cx('icon')}
                                    name={isDetailsExpanded ? WSPIcons.common.ArrowDown : WSPIcons.common.ArrowRight}
                                    size={WSPIconSize.medium16}
                                />
                                <span className={cx('title')}>DETAILS</span>
                            </div>

                            <div className={cx('radioButtons')}>
                                <Tooltip content="Pages">
                                    <button
                                        className={cx('radioButton', {isPressed: isPagesList})}
                                        onClick={() => this._handleRadioButtonClick(ListTypes.Pages)}
                                    >
                                        <MDIcon
                                            className={cx('radioButton_icon')}
                                            name={MDIcons.common.Pages}
                                            size={MDIconSize.medium}
                                        />
                                    </button>
                                </Tooltip>

                                <Tooltip content="Sheets">
                                    <button
                                        className={cx('radioButton', {isPressed: !isPagesList})}
                                        onClick={() => this._handleRadioButtonClick(ListTypes.Sheets)}
                                    >
                                        <MDIcon
                                            className={cx('radioButton_icon')}
                                            name={MDIcons.common.Spreadsheets}
                                            size={MDIconSize.medium}
                                        />
                                    </button>
                                </Tooltip>
                            </div>

                            <Button
                                className={cx('copyButton')}
                                captionClassName={cx('btnCaption')}
                                caption={copyBtnCaption}
                                onClick={this._onCopyBtnClick}
                                style={ButtonStyle.Neutral}
                                size={ButtonSize.BelowMedium}
                                isDisabled={isCopyBtnDisabled}
                            />
                        </Row>

                        <Row
                            className={cx('expandedPart', {isHidden: !isDetailsExpanded})}
                            aRef={this._expandedFooterPartRef}
                        >
                            <div className={cx('expandedPartContent')}>
                                {isPagesList && <Row className={cx('relSheetsToggler')}>
                                    <Checkbox
                                        isChecked={isCopyRelatedSheets}
                                        onClick={this._toggleImportRelatedSheets}
                                        inlineLabel="Import related sheets"
                                        style={CheckboxStyle.Toggler}
                                    />
                                </Row>}

                                <Row className={cx('copyNamePrefix')}>
                                    <InputEdit
                                        value={copyNamePrefix}
                                        label="Prefix for Pages and Sheets"
                                        onChange={this._handleCopyNamePrefixChange}
                                    />
                                </Row>
                            </div>
                        </Row>
                    </div>
                </div>

                {shouldShowLoader && <div className={cx('loaderWrapper')}>
                    <Spinner
                        size={SpinnerSize.Large_48}
                        style={SpinnerStyle.Blue}
                        className={cx('spinner')}
                        weight={SpinnerWeight.Thin}
                    />
                </div>}
            </SurfaceOutlinePanelPartWrapper>
        );
    }

    @bind
    private _renderHeader():JSX.Element
    {
        return <div className={cx('panelHeader')}>
            <span className={cx('panelHeader_title')}>Import from</span>
            <OrganizationsDropdown />
        </div>;
    }

    @bind
    private _renderItems():JSX.Element[]
    {
        const {copyPages} = controllers.ui;

        const copyState = copyPages.getState();
        const {designState: {pages, sheets}, currentListType} = copyState;
        const itemsToRender:Array<PageInfo | SheetInfo> = currentListType === ListTypes.Pages ? pages : sheets;

        const link:string = copyPages.getLinkForSelectedDesign();

        return itemsToRender && itemsToRender.map((itemToRender:PageInfo | SheetInfo) =>
        {
            return <div
                key={itemToRender.id}
                className={cx('item')}
                onClick={() => currentListType === ListTypes.Pages
                    ? this._onPageSelected(itemToRender as PageInfo)
                    : this._onSheetSelected(itemToRender as SheetInfo)
                }
            >
                <div className={cx('checkBox')}>
                    <Checkbox inlineLabel="" isChecked={itemToRender.selected} onClick={null} />
                </div>

                <div className={cx('name')}>{itemToRender.name}</div>

                <a
                    className={cx('link')}
                    href={link}
                    target="_blank"
                    rel="noopener norefferer"
                    onClick={this._getOpenInNewTabLinkClickHandler(
                        currentListType === ListTypes.Pages ? itemToRender.id : '',
                        currentListType === ListTypes.Sheets ? itemToRender.id : ''
                    )}
                >
                    <Tooltip content="Open in new tab">
                        <MDIcon
                            className={cx('linkIcon')}
                            name={MDIcons.common.OpenInNew}
                            size={MDIconSize.medium16}
                        />
                    </Tooltip>
                </a>
            </div>;
        });
    }

    private _getBtnSpinner():JSX.Element
    {
        return <Spinner size={SpinnerSize.Medium_20} style={SpinnerStyle.White} />;
    }

    @bind
    private _handleRadioButtonClick(type:ListTypes):void
    {
        const {copyPages} = controllers.ui;
        copyPages.setCurrentListType(type);
    }

    @bind
    private _getOpenInNewTabLinkClickHandler(pageId:string, sheetId:string):(event:React.MouseEvent<HTMLAnchorElement>) => void
    {
        return (event:React.MouseEvent<HTMLAnchorElement>) =>
        {
            event.stopPropagation();

            LocalStateUtil.storePageAndSheetToOpen({
                page: pageId,
                sheet: sheetId,
            });
        };
    }

    @action.bound
    private _toggleDetailsExpand(e:React.MouseEvent<any>):void
    {
        this._state.isDetailsExpanded = !this._state.isDetailsExpanded;
    }

    @bind
    private _toggleImportRelatedSheets():void
    {
        const {copyPages} = controllers.ui;
        copyPages.toggleCopyRelatedSheets();
    }

    @bind
    private _handleCopyNamePrefixChange(event:React.FormEvent<HTMLInputElement>):void
    {
        const {copyPages} = controllers.ui;
        copyPages.setCopyNamePrefix((event.target as any).value);
    }

    @bind
    private _onPageSelected(page:PageInfo)
    {
        const {copyPages} = controllers.ui;
        copyPages.togglePageSelection(page);
    }

    @bind
    private _onSheetSelected(sheet:SheetInfo)
    {
        const {copyPages} = controllers.ui;
        copyPages.toggleSheetSelection(sheet);
    }

    @bind
    private _onCopyBtnClick()
    {
        const {copyPages} = controllers.ui;
        copyPages.onCopySelectedItems();
    }

    @bind
    private _onCloseClick()
    {
        const {workbenchVisibility} = controllers.ui;
        workbenchVisibility.toggleCopyPagesPanelShown();
    }

    @computed
    private get _selectedPagesCount():number
    {
        const {copyPages} = controllers.ui;
        const state = copyPages.getState();

        if( !state )
            return 0;

        return state.designState.pages.reduce((acc:number, page:PageInfo) =>
        {
            if( page.selected )
                acc++;

            return acc;
        }, 0);
    }

    @computed
    private get _selectedSheetsCount():number
    {
        const {copyPages} = controllers.ui;
        const state = copyPages.getState();

        if( !state )
            return 0;

        return state.designState.sheets.reduce((acc:number, page:PageInfo) =>
        {
            if( page.selected )
                acc++;

            return acc;
        }, 0);
    }
}
